import { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import AdminLayout from '../../components/layout/AdminLayout';
import PayrollLayout from '../../components/layout/PayrollLayout';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';
import { Archive, ArchiveRestore, CalendarDays, CheckCircle2, ChevronDown, ChevronRight, Download, Loader2, Palmtree, Search } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Selectable months from April 2026 up to the last COMPLETED month, newest
// first. The in-progress month appears automatically on the 1st of the next.
const buildMonthOptions = () => {
  const out = [];
  const now = new Date();
  let y = 2026;
  let m = 4;
  while (y < now.getFullYear() || (y === now.getFullYear() && m <= now.getMonth())) {
    const value = `${y}-${String(m).padStart(2, '0')}`;
    out.push({ value, label: new Date(y, m - 1, 1).toLocaleDateString('en-GB', { month: 'long', year: 'numeric' }) });
    m += 1;
    if (m > 12) { m = 1; y += 1; }
  }
  return out.reverse();
};

// UK holiday year start (1 April) — rolls over automatically every year.
const holidayYearStart = () => {
  const now = new Date();
  const y = now.getMonth() + 1 >= 4 ? now.getFullYear() : now.getFullYear() - 1;
  return `${y}-04-01`;
};

// Primary-Admin-only. Worked hours come exclusively from Clock-In History
// (final clock in/out pairs); entitlement = hours × 12.07%.
export default function HolidayEntitlementPage({ payrollMode = false }) {
  const Layout = payrollMode ? PayrollLayout : AdminLayout;
  const [mode, setMode] = useState('total'); // 'total' | 'month' | 'archive'
  const monthOptions = useMemo(buildMonthOptions, []);
  const [month, setMonth] = useState(() => buildMonthOptions()[0]?.value || '2026-04');
  const [data, setData] = useState({ rows: [], period: '' });
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [downloading, setDownloading] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const [monthsCache, setMonthsCache] = useState({});
  const [monthsLoading, setMonthsLoading] = useState(null);
  const [archiveEntries, setArchiveEntries] = useState([]);
  const [paidCycles, setPaidCycles] = useState([]);
  const [downloadedKeys, setDownloadedKeys] = useState({});
  // Persistent (this browser) download marks: 'empId|YYYY-MM' -> ISO date
  const [dlMarks, setDlMarks] = useState(() => {
    try { return JSON.parse(localStorage.getItem('he_excel_downloads')) || {}; } catch { return {}; }
  });
  const markDownloaded = (key) => setDlMarks((d) => {
    const next = { ...d, [key]: new Date().toISOString() };
    try { localStorage.setItem('he_excel_downloads', JSON.stringify(next)); } catch {}
    return next;
  });
  const [archiving, setArchiving] = useState(null);
  const [paying, setPaying] = useState(null);

  const archivedMap = useMemo(() => {
    const map = {};
    for (const e of archiveEntries) map[`${e.employee_id}|${e.month}`] = e;
    return map;
  }, [archiveEntries]);

  const fetchArchive = useCallback(async () => {
    try {
      const [r1, r2] = await Promise.all([
        axios.get(`${API}/admin/holiday-entitlement/archive`),
        axios.get(`${API}/admin/holiday-entitlement/paid`),
      ]);
      setArchiveEntries(r1.data.entries || []);
      setPaidCycles(r2.data.entries || []);
    } catch {
      toast.error('Failed to load archive');
    }
  }, []);

  useEffect(() => { fetchArchive(); }, [fetchArchive]);

  const toggleExpand = async (employeeId) => {
    if (expandedId === employeeId) { setExpandedId(null); return; }
    setExpandedId(employeeId);
    if (!monthsCache[employeeId]) {
      setMonthsLoading(employeeId);
      try {
        const r = await axios.get(`${API}/admin/holiday-entitlement/employee/${employeeId}/months`);
        setMonthsCache((m) => ({ ...m, [employeeId]: r.data.months || [] }));
      } catch {
        toast.error('Failed to load monthly breakdown');
      }
      setMonthsLoading(null);
    }
  };

  const fetchData = useCallback(async () => {
    if (mode === 'archive') return;
    setLoading(true);
    try {
      const params = mode === 'month' ? { month } : {};
      const r = await axios.get(`${API}/admin/holiday-entitlement`, { params });
      setData(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load holiday entitlement');
    }
    setLoading(false);
  }, [mode, month]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const downloadExcel = async (employeeId = null, name = '', monthOverride = null) => {
    const dlKey = employeeId ? `${employeeId}${monthOverride ? `-${monthOverride}` : ''}` : 'all';
    setDownloading(dlKey);
    try {
      const params = {};
      if (monthOverride) params.month = monthOverride;
      else if (mode === 'month') params.month = month;
      if (employeeId) params.employee_id = employeeId;
      const r = await axios.get(`${API}/admin/holiday-entitlement/export.xlsx`, { params, responseType: 'blob' });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url;
      const suffix = monthOverride || (mode === 'month' ? month : 'from_2026-04');
      a.download = employeeId
        ? `Holiday_Entitlement_${name.replace(/\s+/g, '_')}_${suffix}.xlsx`
        : `Holiday_Entitlement_${suffix}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
      if (employeeId) {
        const effMonth = monthOverride || (mode === 'month' ? month : null);
        if (effMonth) {
          setDownloadedKeys((d) => ({ ...d, [`${employeeId}|${effMonth}`]: true }));
          markDownloaded(`${employeeId}|${effMonth}`);
        }
        else if (mode === 'total') setDownloadedKeys((d) => ({ ...d, [`${employeeId}|total`]: true }));
      }
    } catch {
      toast.error('Export failed');
    }
    setDownloading(null);
  };

  const payEmployee = async (employeeId, name) => {
    setPaying(employeeId);
    try {
      await axios.post(`${API}/admin/holiday-entitlement/pay`, { employee_id: employeeId });
      toast.success(`${name} — holiday paid & archived. New cycle starts now.`);
      await Promise.all([fetchData(), fetchArchive()]);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Payment archive failed');
    }
    setPaying(null);
  };

  const undoPay = async (entry) => {
    setPaying(entry.id);
    try {
      await axios.delete(`${API}/admin/holiday-entitlement/paid/${entry.id}`);
      toast.success('Payment undone — hours returned to Total');
      await Promise.all([fetchData(), fetchArchive()]);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Undo failed');
    }
    setPaying(null);
  };

  const downloadPaidExcel = async (entry) => {
    setDownloading(`paid-${entry.id}`);
    try {
      const r = await axios.get(`${API}/admin/holiday-entitlement/paid/${entry.id}/export.xlsx`, { responseType: 'blob' });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Holiday_Paid_${entry.name.replace(/\s+/g, '_')}_${String(entry.period_end).slice(0, 10)}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error('Export failed');
    }
    setDownloading(null);
  };

  const archiveEntry = async (employeeId, entryMonth) => {
    const key = `${employeeId}|${entryMonth}`;
    setArchiving(key);
    try {
      const r = await axios.post(`${API}/admin/holiday-entitlement/archive`, { employee_id: employeeId, month: entryMonth });
      setArchiveEntries((prev) => {
        const others = prev.filter((e) => `${e.employee_id}|${e.month}` !== key);
        return [...others, r.data.entry];
      });
      toast.success('Archived');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Archive failed');
    }
    setArchiving(null);
  };

  const unarchiveEntry = async (entry) => {
    setArchiving(entry.id);
    try {
      await axios.delete(`${API}/admin/holiday-entitlement/archive/${entry.id}`);
      setArchiveEntries((prev) => prev.filter((e) => e.id !== entry.id));
      toast.success('Removed from archive');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Unarchive failed');
    }
    setArchiving(null);
  };

  const archiveByMonth = useMemo(() => {
    const groups = {};
    for (const e of archiveEntries) {
      if (!groups[e.month]) groups[e.month] = [];
      groups[e.month].push(e);
    }
    return Object.keys(groups).sort().reverse().map((m) => ({
      month: m,
      label: new Date(Number(m.slice(0, 4)), Number(m.slice(5, 7)) - 1, 1).toLocaleDateString('en-GB', { month: 'long', year: 'numeric' }),
      entries: groups[m].sort((a, b) => a.name.localeCompare(b.name)),
    }));
  }, [archiveEntries]);

  const filtered = data.rows.filter((r) => !q.trim() || r.name.toLowerCase().includes(q.toLowerCase()));
  const totalWorked = filtered.reduce((s, r) => s + r.worked_hours, 0);
  const totalEnt = filtered.reduce((s, r) => s + r.entitlement_hours, 0);

  const filteredArchive = archiveByMonth
    .map((g) => ({ ...g, entries: g.entries.filter((e) => !q.trim() || e.name.toLowerCase().includes(q.toLowerCase())) }))
    .filter((g) => g.entries.length > 0);

  return (
    <Layout>
      <div className="p-4 sm:p-6 space-y-4 max-w-3xl mx-auto">
        <div>
          <h1 className="font-barlow text-xl sm:text-2xl uppercase tracking-wider text-foreground flex items-center gap-2">
            <Palmtree className="w-5 h-5 text-emerald-400" /> Holiday Entitlement
          </h1>
          <p className="text-sm text-muted-foreground">
            Worked hours come exclusively from Clock-In History (final recorded Clock In/Out pairs), net of automatic break deductions. Entitlement = net hours × 12.07%.
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <div className="flex rounded-sm border border-border overflow-hidden">
            <button
              onClick={() => setMode('total')}
              className={`px-3 py-1.5 text-xs uppercase tracking-wider ${mode === 'total' ? 'bg-primary text-primary-foreground' : 'bg-card text-muted-foreground'}`}
              data-testid="he-mode-total"
            >
              Total · from 1 Apr {holidayYearStart().slice(0, 4)}
            </button>
            <button
              onClick={() => setMode('month')}
              className={`px-3 py-1.5 text-xs uppercase tracking-wider ${mode === 'month' ? 'bg-primary text-primary-foreground' : 'bg-card text-muted-foreground'}`}
              data-testid="he-mode-month"
            >
              Monthly
            </button>
            <button
              onClick={() => setMode('archive')}
              className={`px-3 py-1.5 text-xs uppercase tracking-wider ${mode === 'archive' ? 'bg-primary text-primary-foreground' : 'bg-card text-muted-foreground'}`}
              data-testid="he-mode-archive"
            >
              Archive{(archiveEntries.length + paidCycles.length) ? ` (${archiveEntries.length + paidCycles.length})` : ''}
            </button>
          </div>
          {mode === 'month' && (
            <div className="flex items-center gap-1.5">
              <CalendarDays className="w-4 h-4 text-muted-foreground" />
              <Select value={month} onValueChange={setMonth}>
                <SelectTrigger className="h-9 w-44 bg-card border-border rounded-sm text-sm" data-testid="he-month-select">
                  <SelectValue placeholder="Select month" />
                </SelectTrigger>
                <SelectContent className="bg-card border-border">
                  {monthOptions.map((m) => (
                    <SelectItem key={m.value} value={m.value} data-testid={`he-month-option-${m.value}`}>{m.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          {mode !== 'archive' && (
            <Button
              size="sm"
              onClick={() => downloadExcel()}
              disabled={downloading === 'all' || filtered.length === 0}
              className="rounded-sm bg-emerald-600 hover:bg-emerald-500 text-white ml-auto"
              data-testid="he-export-all-btn"
            >
              {downloading === 'all' ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Download className="w-4 h-4 mr-1.5" /> {mode === 'month' ? 'Month Excel' : 'Full Excel'}</>}
            </Button>
          )}
        </div>

        <div className="relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search employee name…"
            className="pl-9 bg-card border-border rounded-sm"
            data-testid="he-search-input"
          />
        </div>

        {mode === 'archive' ? (
          <>
            <p className="text-xs uppercase tracking-wider text-muted-foreground" data-testid="he-archive-count">
              Payroll Holiday Archive · {paidCycles.length} paid cycles · {archiveEntries.length} monthly archived
            </p>
            {paidCycles.length > 0 && (
              <div className="space-y-2" data-testid="he-paid-section">
                <p className="text-xs font-semibold uppercase tracking-wider text-amber-400 border-b border-border pb-1">
                  Paid Holiday — Total Cycles
                </p>
                {paidCycles
                  .filter((e) => !q.trim() || e.name.toLowerCase().includes(q.toLowerCase()))
                  .map((e) => (
                    <Card key={e.id} className="border-amber-500/30 bg-amber-500/5" data-testid={`he-paid-row-${e.id}`}>
                      <CardContent className="p-3 flex flex-wrap items-center gap-2">
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-medium truncate">{e.name}</p>
                          <p className="text-[11px] text-muted-foreground">
                            Cycle: <b className="text-foreground">{String(e.period_start).slice(0, 10)} → {String(e.period_end_display || e.period_end).slice(0, 10)}</b>
                            <span className="mx-1.5">·</span>
                            Worked: <b className="text-foreground">{Number(e.worked_hours).toFixed(2)} h</b>
                            <span className="mx-1.5">·</span>
                            Paid: <b className="text-amber-400">{Number(e.entitlement_hours).toFixed(2)} h</b>
                            <span className="mx-1.5">·</span>
                            {e.paid_at ? new Date(e.paid_at).toLocaleDateString('en-GB') : ''} {e.paid_by ? `by ${e.paid_by}` : ''}
                          </p>
                        </div>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => downloadPaidExcel(e)}
                          disabled={downloading === `paid-${e.id}`}
                          className="h-7 px-2 rounded-sm text-[11px]"
                          data-testid={`he-paid-export-${e.id}`}
                        >
                          {downloading === `paid-${e.id}` ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <><Download className="w-3.5 h-3.5 mr-1" /> Excel</>}
                        </Button>
                        {!payrollMode && (
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => undoPay(e)}
                          disabled={paying === e.id}
                          className="h-7 px-2 rounded-sm text-[11px] text-amber-400 hover:text-amber-300"
                          data-testid={`he-undo-pay-${e.id}`}
                        >
                          {paying === e.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <><ArchiveRestore className="w-3.5 h-3.5 mr-1" /> Undo payment</>}
                        </Button>
                        )}
                      </CardContent>
                    </Card>
                  ))}
              </div>
            )}
            {filteredArchive.length === 0 ? (
              <Card className="border-border"><CardContent className="py-12 text-center text-sm text-muted-foreground">
                Nothing archived yet. In Monthly view, download an employee's Excel then press Archive.
              </CardContent></Card>
            ) : (
              <div className="space-y-4">
                {filteredArchive.map((g) => (
                  <div key={g.month} className="space-y-2" data-testid={`he-archive-group-${g.month}`}>
                    <p className="text-xs font-semibold uppercase tracking-wider text-emerald-400 border-b border-border pb-1">{g.label}</p>
                    {g.entries.map((e) => (
                      <Card key={e.id} className="border-border" data-testid={`he-archive-row-${e.id}`}>
                        <CardContent className="p-3 flex flex-wrap items-center gap-2">
                          <div className="min-w-0 flex-1">
                            <p className="text-sm font-medium truncate">{e.name}</p>
                            <p className="text-[11px] text-muted-foreground">
                              Worked: <b className="text-foreground">{Number(e.worked_hours).toFixed(2)} h</b>
                              <span className="mx-1.5">·</span>
                              Entitlement: <b className="text-emerald-400">{Number(e.entitlement_hours).toFixed(2)} h</b>
                              <span className="mx-1.5">·</span>
                              Archived {e.archived_at ? new Date(e.archived_at).toLocaleDateString('en-GB') : ''}
                            </p>
                          </div>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => downloadExcel(e.employee_id, e.name, e.month)}
                            disabled={downloading === `${e.employee_id}-${e.month}`}
                            className="h-7 px-2 rounded-sm text-[11px]"
                            data-testid={`he-archive-export-${e.id}`}
                          >
                            {downloading === `${e.employee_id}-${e.month}` ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <><Download className="w-3.5 h-3.5 mr-1" /> Excel</>}
                          </Button>
                          {!payrollMode && (
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => unarchiveEntry(e)}
                            disabled={archiving === e.id}
                            className="h-7 px-2 rounded-sm text-[11px] text-amber-400 hover:text-amber-300"
                            data-testid={`he-unarchive-${e.id}`}
                          >
                            {archiving === e.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <><ArchiveRestore className="w-3.5 h-3.5 mr-1" /> Unarchive</>}
                          </Button>
                          )}
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                ))}
              </div>
            )}
          </>
        ) : (
          <>
            <p className="text-xs uppercase tracking-wider text-muted-foreground" data-testid="he-period-label">
              {data.period}{filtered.length ? ` · ${filtered.length} employees` : ''}{mode === 'total' && paidCycles.length ? ` · ${paidCycles.length} paid cycles in archive` : ''}
            </p>

            {loading ? (
              <div className="py-16 text-center text-muted-foreground animate-pulse">Loading…</div>
            ) : filtered.length === 0 ? (
              <Card className="border-border"><CardContent className="py-12 text-center text-sm text-muted-foreground">
                No worked hours found in Clock-In History for this period.
              </CardContent></Card>
            ) : (
              <div className="space-y-2">
                <Card className="border-emerald-500/40 bg-emerald-500/5">
                  <CardContent className="p-3 flex flex-wrap items-center gap-x-6 gap-y-1 text-sm">
                    <span className="text-muted-foreground text-xs uppercase tracking-wider">Totals</span>
                    <span data-testid="he-total-worked"><b>{totalWorked.toFixed(2)}</b> h worked</span>
                    <span className="text-emerald-400" data-testid="he-total-entitlement"><b>{totalEnt.toFixed(2)}</b> h entitlement</span>
                  </CardContent>
                </Card>
                {filtered.map((r) => {
                  const rowKey = `${r.employee_id}|${month}`;
                  const isArchived = mode === 'month' && archivedMap[rowKey];
                  const canArchive = !payrollMode && mode === 'month' && !isArchived && downloadedKeys[rowKey];
                  const canPay = !payrollMode && mode === 'total' && downloadedKeys[`${r.employee_id}|total`];
                  const newCycle = mode === 'total' && r.period_start && String(r.period_start).slice(0, 10) > holidayYearStart();
                  return (
                    <Card key={r.employee_id} className="border-border" data-testid={`he-row-${r.employee_id}`}>
                      <CardContent className="p-3">
                        <div className="flex flex-wrap items-center gap-2">
                          <button
                            type="button"
                            onClick={() => toggleExpand(r.employee_id)}
                            className="min-w-0 flex-1 basis-full sm:basis-auto text-left group"
                            data-testid={`he-name-${r.employee_id}`}
                            title="Show monthly breakdown"
                          >
                            <p className="text-sm font-medium truncate group-hover:text-primary flex items-center gap-1.5">
                              {expandedId === r.employee_id
                                ? <ChevronDown className="w-3.5 h-3.5 text-primary shrink-0" />
                                : <ChevronRight className="w-3.5 h-3.5 text-muted-foreground shrink-0" />}
                              <span className="truncate underline-offset-2 group-hover:underline">{r.name}</span>
                              {isArchived && (
                                <span className="text-[9px] uppercase tracking-wider bg-emerald-500/15 text-emerald-400 border border-emerald-500/40 rounded-sm px-1.5 py-0.5 shrink-0" data-testid={`he-archived-badge-${r.employee_id}`}>
                                  Archived
                                </span>
                              )}
                              {newCycle && (
                                <span className="text-[9px] uppercase tracking-wider bg-amber-500/15 text-amber-400 border border-amber-500/40 rounded-sm px-1.5 py-0.5 shrink-0" data-testid={`he-cycle-badge-${r.employee_id}`}>
                                  New cycle since {String(r.period_start).slice(8, 10)}/{String(r.period_start).slice(5, 7)}/{String(r.period_start).slice(0, 4)}
                                </span>
                              )}
                            </p>
                            <p className="text-[11px] text-muted-foreground pl-5">
                              Worked: <b className="text-foreground">{r.worked_hours.toFixed(2)} h</b>
                              <span className="mx-1.5">·</span>
                              Entitlement: <b className="text-emerald-400">{r.entitlement_hours.toFixed(2)} h</b>
                            </p>
                          </button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => downloadExcel(r.employee_id, r.name)}
                            disabled={downloading === r.employee_id}
                            className={`h-7 px-2 rounded-sm text-[11px] ${mode === 'month' && dlMarks[`${r.employee_id}|${month}`] ? 'border-red-500/50 text-red-400 hover:text-red-300' : ''}`}
                            title={mode === 'month' && dlMarks[`${r.employee_id}|${month}`] ? `Downloaded on ${new Date(dlMarks[`${r.employee_id}|${month}`]).toLocaleDateString('en-GB')}` : undefined}
                            data-testid={`he-export-${r.employee_id}`}
                          >
                            {downloading === r.employee_id
                              ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                              : mode === 'month' && dlMarks[`${r.employee_id}|${month}`]
                                ? <><CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Excel</>
                                : <><Download className="w-3.5 h-3.5 mr-1" /> Excel</>}
                          </Button>
                          {canArchive && (
                            <Button
                              size="sm"
                              onClick={() => archiveEntry(r.employee_id, month)}
                              disabled={archiving === rowKey}
                              className="h-7 px-2 rounded-sm text-[11px] bg-emerald-600 hover:bg-emerald-500 text-white"
                              data-testid={`he-archive-btn-${r.employee_id}`}
                            >
                              {archiving === rowKey ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <><Archive className="w-3.5 h-3.5 mr-1" /> Archive</>}
                            </Button>
                          )}
                          {canPay && (
                            <Button
                              size="sm"
                              onClick={() => payEmployee(r.employee_id, r.name)}
                              disabled={paying === r.employee_id}
                              className="h-7 px-2 rounded-sm text-[11px] bg-amber-600 hover:bg-amber-500 text-white"
                              data-testid={`he-pay-btn-${r.employee_id}`}
                            >
                              {paying === r.employee_id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <><Archive className="w-3.5 h-3.5 mr-1" /> Paid → Archive</>}
                            </Button>
                          )}
                        </div>
                        {expandedId === r.employee_id && (
                          <div className="mt-2 ml-5 border-l-2 border-border pl-3 space-y-1.5" data-testid={`he-months-${r.employee_id}`}>
                            {monthsLoading === r.employee_id ? (
                              <p className="text-xs text-muted-foreground animate-pulse py-1">Loading months…</p>
                            ) : (monthsCache[r.employee_id] || []).length === 0 ? (
                              <p className="text-xs text-muted-foreground py-1">No monthly worked hours found.</p>
                            ) : (
                              (monthsCache[r.employee_id] || []).map((m) => {
                                const dlAt = dlMarks[`${r.employee_id}|${m.month}`];
                                return (
                                <div key={m.month} className="flex flex-wrap items-center gap-x-3 gap-y-1 py-0.5" data-testid={`he-month-row-${r.employee_id}-${m.month}`}>
                                  <span className="text-xs font-medium w-28">{m.label}</span>
                                  <span className="text-[11px] text-muted-foreground">
                                    Worked: <b className="text-foreground">{m.worked_hours.toFixed(2)} h</b>
                                  </span>
                                  <span className="text-[11px] text-muted-foreground">
                                    Entitlement: <b className="text-emerald-400">{m.entitlement_hours.toFixed(2)} h</b>
                                  </span>
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    onClick={() => downloadExcel(r.employee_id, r.name, m.month)}
                                    disabled={downloading === `${r.employee_id}-${m.month}`}
                                    className={`h-6 px-1.5 rounded-sm text-[10px] ${dlAt ? 'text-red-400 hover:text-red-300' : 'text-emerald-400 hover:text-emerald-300'}`}
                                    title={dlAt ? `Downloaded on ${new Date(dlAt).toLocaleDateString('en-GB')} at ${new Date(dlAt).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}` : 'Not downloaded yet'}
                                    data-testid={`he-month-export-${r.employee_id}-${m.month}`}
                                  >
                                    {downloading === `${r.employee_id}-${m.month}`
                                      ? <Loader2 className="w-3 h-3 animate-spin" />
                                      : dlAt
                                        ? <><CheckCircle2 className="w-3 h-3 mr-1" /> Excel</>
                                        : <><Download className="w-3 h-3 mr-1" /> Excel</>}
                                  </Button>
                                  {archivedMap[`${r.employee_id}|${m.month}`] ? (
                                    <span className="text-[9px] uppercase tracking-wider bg-emerald-500/15 text-emerald-400 border border-emerald-500/40 rounded-sm px-1.5 py-0.5">Archived</span>
                                  ) : downloadedKeys[`${r.employee_id}|${m.month}`] && !payrollMode ? (
                                    <Button
                                      size="sm"
                                      variant="ghost"
                                      onClick={() => archiveEntry(r.employee_id, m.month)}
                                      disabled={archiving === `${r.employee_id}|${m.month}`}
                                      className="h-6 px-1.5 rounded-sm text-[10px] text-emerald-400 hover:text-emerald-300"
                                      data-testid={`he-month-archive-${r.employee_id}-${m.month}`}
                                    >
                                      {archiving === `${r.employee_id}|${m.month}` ? <Loader2 className="w-3 h-3 animate-spin" /> : <><Archive className="w-3 h-3 mr-1" /> Archive</>}
                                    </Button>
                                  ) : null}
                                </div>
                              );})
                            )}
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}
          </>
        )}
      </div>
    </Layout>
  );
}
